import { useEffect, useState } from 'react';
import q from 'query-string';

type Options<T> = {
  // Parse the url string into a potentially more useful type
  parse?: (value: string) => T;
  // Transform the value into a string that can be stored in the URL
  // If null is returned, the value is ommitted from being stored in the URL
  stringify?: (value: T) => string | string[] | null;
};

/**
 * Modified version of `useState` that syncs the current state
 * with URL query params.
 * A query param in the URL on page load will override `initialState`.
 *
 * `key` is a string key for the query param.
 *
 * `opts` is an object that allows custom parsing/stringifying behavior
 * between the string value in the URL query param and state.
 */
const useUrlState = function <T>(
  initialState: T,
  key: string,
  opts?: Options<T>,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const query = q.parse(window.location.search);

  let initial;
  if (typeof opts?.parse === 'function' && query[key] != null) {
    // TypeScript doesn't know that query[key] can't be null or undefined
    // here for some reason
    initial = opts.parse(query[key] as string);
  } else {
    initial = query[key] ?? initialState;
  }

  const [state, setState] = useState(initial as T);

  let qValue: string | null;
  if (typeof opts?.stringify === 'function') {
    // @ts-expect-error ts-migrate(2322) FIXME: Type 'string | string[] | null' is not assignable ... Remove this comment to see the full error message
    qValue = opts.stringify(state);
  } else {
    // This cast isn't strictly necessary, since q.stringify
    // will allow just about anything, but TypeScript gets big mad without it
    qValue = String(state);
  }

  useEffect(() => {
    const url = window.location.origin + window.location.pathname;

    // Update the current key with our new state value
    if (qValue != null) {
      query[key] = qValue;
    } else {
      delete query[key];
    }

    history.replaceState(
      null,
      '',
      `${url}?${q.stringify(query, { encode: false })}`,
    );
    // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return [state, setState];
};

export default useUrlState;
